import React, { useEffect, useState, useMemo } from 'react';
import { Button, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce, useUpdateEffect } from 'usehooks-ts';
import DataTable from 'react-data-table-component';
import { TableColumn } from 'react-data-table-component';
import { compact } from 'lodash';

import { Collaboration } from '@models/Collaboration';
import { formatDateTime } from '@utils/datetime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPencil } from '@fortawesome/free-solid-svg-icons';
import { formatPercentage } from '@utils/currency';

import { selectFeatureCollaboration } from './selector';
import {
  collaborationsFetch,
  CollaborationsFetchPayload,
  stateClear,
} from './action';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

type Props = Pick<CollaborationsFetchPayload, 'collaboratableType' | 'collaboratableId'> & {
  onEdit?: (collaboration: Collaboration) => void;
  onDelete?: (collaboration: Collaboration) => void;
};

const CollaborationListView = ({ collaboratableType, collaboratableId, onEdit, onDelete }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { collaborations, isLoading, isReload, pagination } = useSelector(selectFeatureCollaboration);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const canManage = !!onEdit || !!onDelete;

  const columns = useMemo(() => compact([
    canManage && {
      cell: (collaboration) => (
        <Stack direction="horizontal" gap={1}>
          {!!onEdit && (
            <Button onClick={() => onEdit(collaboration)} variant="outline-primary" size="sm">
              <FontAwesomeIcon icon={faPencil} />
            </Button>
          )}
          {!!onDelete && (
            <Button onClick={() => onDelete(collaboration)} variant="outline-danger" size="sm">
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          )}
        </Stack>
      ),
      button: true,
      center: true,
    },
    {
      name: t('common:createdDate'),
      selector: ({ createdAt }) => formatDateTime(createdAt),
      minWidth: '180px',
    },
    {
      name: "Collaboration",
      selector: ({ collaborator }) => collaborator.name,
    },
    {
      name: "Commission",
      selector: ({ commissionPercentage }) => formatPercentage(commissionPercentage),
    },
  ]) as TableColumn<Collaboration>[], [canManage, onEdit, onDelete]);

  useEffect(() => {
    const payload: CollaborationsFetchPayload = { collaboratableType, collaboratableId, page, perPage };
    dispatch(collaborationsFetch(payload));
  }, [collaboratableType, collaboratableId, page, perPage]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  useUpdateEffect(() => {
    const payload: CollaborationsFetchPayload = { collaboratableType, collaboratableId, page: 1, perPage };
    dispatch(collaborationsFetch(payload));
  }, [isReload]);

  return (
    <DataTable
      columns={columns}
      data={collaborations ?? []}
      customStyles={customStyles}
      fixedHeader
      pagination
      paginationServer
      progressPending={isLoading}
      paginationPerPage={perPage}
      paginationTotalRows={pagination?.total ?? 0}
      onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
      onChangePage={(newPage) => setPage(newPage)}
    />
  );
};

export { CollaborationListView };
